// Genomes (HER-2, ARCH §28.4). PURE (ENG-1).
//
// A genome is a few bytes laid out by the species' gene table, in table order: a
// Mendelian gene is 2 bytes (the two allele indices, NORMALIZED ASCENDING so `(1,0)`
// and `(0,1)` are one genotype and one encoding); a polygenic gene is 1 byte (0..255,
// mapped onto its range at expression). It travels as base64 with no padding — in a
// save row, a spawn frame, a seed's item components.
//
// Everything random comes from a caller-supplied `Rng` (forked by `forkRng(seed,
// 'her:roll', id)`), so a roll is a pure function of (seed, id) — which is what lets
// two worlds that toggle the rule on at different ticks agree on every genome (§28.2).
import { tripleAverage } from '../game/horse';
import type { Rng } from '../engine/rng';
import type { Gene } from './genes';

/** Bytes a table's genome occupies. */
export function genomeLength(genes: readonly Gene[]): number {
  let n = 0;
  for (const g of genes) n += g.kind === 'mendelian' ? 2 : 1;
  return n;
}

// ── base64, no padding ────────────────────────────────────────────────────────
// Hand-rolled rather than `btoa`: the sim is pure and must behave identically in a
// worker, a browser and Node, with no dependence on a global's existence or encoding.
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INDEX = new Map([...B64].map((c, i) => [c, i] as const));

export function encodeGenome(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = i + 1 < bytes.length ? bytes[i + 1]! : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2]! : 0;
    const n = (a << 16) | (b << 8) | c;
    out += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]!;
    if (i + 1 < bytes.length) out += B64[(n >> 6) & 63]!;
    if (i + 2 < bytes.length) out += B64[n & 63]!;
  }
  return out;
}

/** Raw base64 decode; null on a character outside the alphabet or an impossible
 *  length. Exported for callers that decode a genome's bytes generically, without
 *  a gene table to normalize Mendelian pairs against (`heredity/crops.ts`
 *  `quintileKey` — a crop genome is all-polygenic, so there is nothing to
 *  normalize). Table-aware callers use `decodeGenome` instead. */
export function fromBase64(s: string): Uint8Array | null {
  if (s.length % 4 === 1) return null;
  const out: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const ch of s) {
    const v = B64_INDEX.get(ch);
    if (v === undefined) return null;
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((acc >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}

/** Decode a genome for this table. Null (E-51 — the caller re-rolls) when the text is
 *  not base64, the length does not match the table, or a Mendelian byte names an allele
 *  the gene does not have. A valid genome comes back with its pairs normalized. */
export function decodeGenome(text: string, genes: readonly Gene[]): Uint8Array | null {
  const bytes = fromBase64(text);
  if (!bytes || bytes.length !== genomeLength(genes)) return null;
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') {
      const x = bytes[at]!, y = bytes[at + 1]!;
      if (x >= g.alleles.length || y >= g.alleles.length) return null;
      bytes[at] = Math.min(x, y);
      bytes[at + 1] = Math.max(x, y);
      at += 2;
    } else {
      at += 1;
    }
  }
  return bytes;
}

/** Pick an allele index by a gene's wild-type frequencies (one draw). */
function pickAllele(freq: readonly number[], rng: Rng): number {
  const f = rng.nextFloat();
  let acc = 0;
  for (let i = 0; i < freq.length; i++) {
    acc += freq[i]!;
    if (f < acc) return i;
  }
  return freq.length - 1; // float rounding at the top edge
}

/** A wild-type genome (§28.4): Mendelian by `freq`, two independent draws, normalized;
 *  polygenic `round(255 · tripleAverage(f, f, f))` — the shape a classic horse's
 *  attributes are drawn from. Draws in table order, so the layout IS the draw order. */
export function rollGenome(genes: readonly Gene[], rng: Rng): Uint8Array {
  const out = new Uint8Array(genomeLength(genes));
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') {
      const x = pickAllele(g.freq, rng);
      const y = pickAllele(g.freq, rng);
      out[at] = Math.min(x, y);
      out[at + 1] = Math.max(x, y);
      at += 2;
    } else {
      out[at] = Math.round(255 * tripleAverage(rng.nextFloat(), rng.nextFloat(), rng.nextFloat()));
      at += 1;
    }
  }
  return out;
}

/** The expressed scalars an entity (or a crop) carries — computed ONCE at spawn/load/
 *  toggle from its genome, never per tick (D-TF-120). §28.4's list, plus the crop
 *  scalars §28.10 reads. */
export interface Traits {
  readonly speedMul: number;
  readonly eggMul: number;
  readonly scale: number;
  readonly fleeMul: number;
  readonly wanderMul: number;
  readonly approachP: number;
  readonly cohesion: number;
  readonly startleP: number;
  readonly lifespanTicks: number;
  readonly jumpMul: number;
  readonly healthMul: number;
  readonly regrowMul: number;
}

/** Rule off, or a species with no table: every scalar at the value that changes
 *  nothing (§28.4). Frozen and shared. */
export const IDENTITY: Traits = Object.freeze({
  speedMul: 1, eggMul: 1, scale: 1, fleeMul: 1, wanderMul: 1, approachP: 0,
  cohesion: 0, startleP: 0, lifespanTicks: 0, jumpMul: 1, healthMul: 1, regrowMul: 1,
});
